import {
  ForwardedRef,
  ReactNode,
  Ref,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useRef,
  useMemo,
} from 'react';
import { Form as MuiForm } from '@rjsf/mui';
import Form from '@rjsf/core';
import { customizeValidator } from '@rjsf/validator-ajv8';
import { JSONSchema7 } from 'json-schema';
import { UiSchema } from '@rjsf/utils/src/types.ts';
import templates from 'components/overwrite/FormTemplate';
import CheckboxWidget from './CheckboxWidget';
import { CustomValidator, ErrorSchema, RegistryWidgetsType, RJSFSchema, RJSFValidationError } from '@rjsf/utils';
import { customFormatDefinitions, getErrorMessage, isCustomFormat } from './CustomFormats.ts';
import { get as getByPath } from 'lodash';
import { useKeyboard } from 'hooks/useKeyboard.ts';
import { FormBlockContext } from './FormBlock.tsx';
import { Ajv2019 } from 'ajv/dist/2019';

const customWidgets: RegistryWidgetsType = { CheckboxWidget };

type Props<T> = {
  schema: JSONSchema7;
  uiSchema?: UiSchema;
  onSubmit: (formData: T) => void;
  onChange?: (formData: T) => void;
  initialValues?: T;
  children?: ReactNode;
  isLoading?: boolean;
  formId: string;
  getRef?: (ref: Ref<Form<T>>) => void;
  disabled?: boolean;
  className?: string;
  formContext?: FormBlockContext;
  validator?: CustomValidator<T>;
  extraErrors?: ErrorSchema;
};

const CustomFormInner = <T extends object>(
  {
    onSubmit,
    onChange,
    schema,
    uiSchema,
    initialValues = {} as T,
    isLoading = false,
    children,
    formId,
    getRef,
    disabled = false,
    className,
    formContext,
    validator,
    extraErrors,
  }: Props<T>,
  ref: ForwardedRef<unknown>,
) => {
  const [formData, setFormData] = useState<T>(initialValues);

  const formRef = useRef<Form<any, RJSFSchema, any>>();

  const customValidator = useMemo(
    () =>
      customizeValidator({
        AjvClass: Ajv2019,
        ajvOptionsOverrides: { useDefaults: true, verbose: true, allErrors: true },
        customFormats: customFormatDefinitions,
      }),
    [],
  );

  const handleSubmit = useCallback(() => {
    if (isLoading) {
      return;
    }
    onSubmit(formData);
  }, [formData, onSubmit, isLoading]);

  const handleOnChange = useCallback(
    (newFormData: T) => {
      setFormData(newFormData);
      if (onChange) {
        onChange(newFormData);
      }
    },
    [onChange],
  );

  const transformErrors = useCallback(
    (errors: RJSFValidationError[]): RJSFValidationError[] => {
      const seen = new Set();
      return errors
        .map((error) => {
          if (error.params?.format && isCustomFormat(error.params.format)) {
            return {
              ...error,
              message: getErrorMessage(error.params.format, getByPath(formData, error.property?.slice(1) ?? '.')),
            };
          }
          return error;
        })
        .filter((error) => {
          const key = `${error.property}|${error.message}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    },
    [formData],
  );

  const handleKeyboardSubmit = useCallback(() => {
    formRef?.current?.submit();
  }, []);

  if (getRef) {
    getRef(formRef as Ref<Form<T, RJSFSchema, any>>);
  }

  useImperativeHandle(
    ref,
    () => ({
      validateForm(): boolean {
        return formRef?.current?.validateForm() ?? false;
      },
    }),
    [],
  );

  const keyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && !disabled) {
        e.preventDefault();
        handleKeyboardSubmit();
      }
    },
    [handleKeyboardSubmit],
  );

  useKeyboard({
    keyDown,
  });

  return (
    <MuiForm
      templates={templates}
      widgets={customWidgets}
      schema={schema}
      uiSchema={uiSchema}
      formData={formData}
      onChange={({ formData }) => handleOnChange(formData as T)}
      validator={customValidator}
      customValidate={validator}
      onSubmit={handleSubmit}
      id={formId}
      ref={formRef as Ref<Form<any, RJSFSchema, any>>}
      disabled={disabled}
      className={className}
      transformErrors={transformErrors}
      extraErrors={extraErrors}
      formContext={formContext}
      experimental_defaultFormStateBehavior={{ constAsDefaults: 'never' }}
    >
      <>{children}</>
    </MuiForm>
  );
};

const CustomForm = forwardRef(CustomFormInner) as <T>(
  props: Props<T> & { ref?: React.ForwardedRef<Form> },
) => ReturnType<typeof CustomFormInner>;

export default CustomForm;
